import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";

export function SEOHead({ title, description, path = "", ogImage }) {
  const { i18n } = useTranslation();
  const base = "https://onwardflightickets.com";
  const url = `${base}${path}`;
  const img = ogImage || `${base}/og-image.png`;

  useEffect(() => {
    const dir = i18n.language === "ar" ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content="OnwardFlighTickets" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
    </Helmet>
  );
}

export function OrganizationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "OnwardFlighTickets",
    url: "https://onwardflightickets.com",
    description: "Get your verified onward flight ticket in under 60 seconds. Real flight data, verifiable booking reference, PDF delivery.",
    contactPoint: { "@type": "ContactPoint", contactType: "customer service", email: "support@onwardflightickets.com", availableLanguage: ["en", "fr", "es", "de", "pt", "ar"] },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", bestRating: "5", ratingCount: "2847" },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function WebSiteSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "OnwardFlighTickets",
    url: "https://onwardflightickets.com",
    potentialAction: {
      "@type": "SearchAction",
      target: { "@type": "EntryPoint", urlTemplate: "https://onwardflightickets.com/search-results?from={departure}&to={arrival}" },
      "query-input": "required name=departure",
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function FAQSchema({ faqs }) {
  if (!faqs?.length) return null;
  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function BreadcrumbSchema({ items }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function ArticleSchema({ title, description, url, datePublished }) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url,
    datePublished: datePublished || "2026-01-01",
    author: { "@type": "Organization", name: "OnwardFlighTickets" },
    publisher: { "@type": "Organization", name: "OnwardFlighTickets", url: "https://onwardflightickets.com" },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function ProductServiceSchema() {
  const product = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Onward Flight Ticket Reservation",
    description: "Verified onward flight ticket reservation with real flight data, instant PDF delivery, and verifiable booking reference.",
    offers: { "@type": "AggregateOffer", lowPrice: "12", highPrice: "26", priceCurrency: "USD", offerCount: "3" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "25000", bestRating: "5" },
  };
  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Onward Flight Ticket Service",
    provider: { "@type": "Organization", name: "OnwardFlighTickets" },
    serviceType: "Flight Reservation Service",
    areaServed: "Worldwide",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Onward Ticket Plans",
      itemListElement: [
        { "@type": "Offer", name: "Standard (48h)", price: "12", priceCurrency: "USD" },
        { "@type": "Offer", name: "Extended (7 days)", price: "18", priceCurrency: "USD" },
        { "@type": "Offer", name: "Premium (14 days)", price: "26", priceCurrency: "USD" },
      ],
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(product)}</script>
      <script type="application/ld+json">{JSON.stringify(service)}</script>
    </Helmet>
  );
}
